import { keyframes } from '@emotion/react';
import { useCallback, useEffect, useRef } from 'react';
import { useAtom } from 'jotai';
import { CHAT_LAUNCHER_TOOLTIP } from '../../constants/chat';
import { useChatHint } from '../../hooks/useChatHint';
import { useReducedMotion } from '../../hooks/useReducedMotion';
import { useAuth } from '../../services/auth0';
import { useFlags } from '../../services/launchDarkly';
import { chatPanelOpenState } from '../../store/global.store';
import { ChatLauncherNudge } from '../Chat/ChatLauncherNudge';
import { ChatPanel } from '../Chat/ChatPanel';
import { MontyPeekImage } from '../Chat/MontyAvatar';
import { MBox, MIconButton, MTooltip } from '../Monetize';

const entranceBounce = keyframes`
  0%   { transform: translateY(160px) scale(0.4);  opacity: 0; }
  35%  { transform: translateY(-30px) scale(1.12); opacity: 1; }
  50%  { transform: translateY(14px)  scale(0.94); }
  65%  { transform: translateY(-18px) scale(1.07); }
  78%  { transform: translateY(8px)   scale(0.97); }
  88%  { transform: translateY(-5px)  scale(1.03); }
  95%  { transform: translateY(2px)   scale(0.99); }
  100% { transform: translateY(0)     scale(1); }
`;

const hintWiggle = keyframes`
  0%, 100% { transform: rotate(0deg); }
  25%      { transform: rotate(-8deg); }
  60%      { transform: rotate(6deg); }
`;

export const AppAIChatIcon = () => {
  const [isChatPanelOpen, setIsChatPanelOpen] = useAtom(chatPanelOpenState);
  const { isAuthenticated, tenantId, userId } = useAuth();
  const { useAiChat } = useFlags();
  const reducedMotion = useReducedMotion();
  const launcherRef = useRef<HTMLButtonElement>(null);
  const wasChatPanelOpenRef = useRef(false);

  // The launcher unmounts while the panel is open, so closing the panel would
  // otherwise leave keyboard focus on <body>, far from where the user was.
  useEffect(() => {
    if (wasChatPanelOpenRef.current && !isChatPanelOpen) {
      launcherRef.current?.focus();
    }
    wasChatPanelOpenRef.current = isChatPanelOpen;
  }, [isChatPanelOpen]);

  const isLauncherEnabled = Boolean(
    isAuthenticated && tenantId && userId && useAiChat,
  );

  const { message, dismissHint, disableHints, hintShownByOpeningChat } =
    useChatHint({
      enabled: isLauncherEnabled,
      userId,
      isChatPanelOpen,
    });

  /**
   * Opening the chat is the discovery a hint exists to cause, so spend the
   * session budget here too -- someone who found Monty on their own should
   * not be nudged towards him afterwards.
   */
  const handleOpenChat = useCallback(() => {
    hintShownByOpeningChat();
    setIsChatPanelOpen(true);
  }, [hintShownByOpeningChat, setIsChatPanelOpen]);

  if (!isLauncherEnabled) {
    return null;
  }

  return (
    <>
      {!isChatPanelOpen && (
        <MBox
          position="fixed"
          right="2rem"
          bottom="2rem"
          zIndex={5}
          animation={
            reducedMotion ? undefined : `${entranceBounce} 1.4s ease-out both`
          }
        >
          {message && (
            <ChatLauncherNudge
              message={message}
              onDismiss={dismissHint}
              onDisableHints={disableHints}
              onOpenChat={handleOpenChat}
              reducedMotion={reducedMotion}
            />
          )}
          <MTooltip label={CHAT_LAUNCHER_TOOLTIP} placement="left" hasArrow>
            <MIconButton
              ref={launcherRef}
              aria-label="Open AI chat"
              icon={<MontyPeekImage />}
              variant="ai"
              boxSize="3rem"
              minW="3rem"
              p={0}
              borderRadius="full"
              overflow="hidden"
              alignItems="flex-end"
              boxShadow="0 4px 12px rgba(140, 50, 255, 0.25)"
              animation={
                message && !reducedMotion
                  ? `${hintWiggle} 600ms ease-in-out`
                  : undefined
              }
              onClick={handleOpenChat}
            />
          </MTooltip>
        </MBox>
      )}
      <ChatPanel />
    </>
  );
};
